/**
 * Creates a throttled version of the provided function that only invokes the function at most once every `wait` milliseconds.
 * The throttled function also has a `cancel` method to clear any pending executions.
 *
 * @param func - The function to throttle.
 * @param wait - The number of milliseconds to wait before allowing the function to be called again.
 * @returns A throttled version of the function with a `cancel` method.
 */
export function throttle<T extends (...args: unknown[]) => void>(func: T, wait: number): T & { cancel: () => void } {
  let timeout: ReturnType<typeof setTimeout> | null = null
  let lastArgs: Parameters<T> | null = null

  const throttled = (...args: Parameters<T>) => {
    if (timeout) {
      lastArgs = args
      return
    }

    func(...args)
    timeout = setTimeout(() => {
      timeout = null
      if (lastArgs) {
        func(...lastArgs)
        lastArgs = null
      }
    }, wait)
  }

  throttled.cancel = () => {
    if (timeout) clearTimeout(timeout)
    timeout = null
    lastArgs = null
  }

  return throttled as T & { cancel: () => void }
}
